import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import { CONVERSATION_FACTS_DIMENSIONS, ROUTING_MEASURES } from 'cube/constants'
import { DeadEndChatsQuery } from 'cube/queries'
import { isNil } from 'ramda'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import {
  type RoutingKPIMetric,
  resolveCubeKPIPercentage,
  resolveKPIScore,
  resolveTotalRecords,
} from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

import { KPI_SCORES } from './DeadEndChats.constants'

/***
 * Hook that cube to fetch dead ends chats data
 *
 * @returns {RoutingKPIMetric}
 */
export const useDeadEndChats = (): RoutingKPIMetric => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()

  const cubeQuery = new CubeQueryBuilder({ query: DeadEndChatsQuery })
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withFilters(pageFilters)
    .build()

  const { data, isLoading, isError } = useCubeQuery({
    query: cubeQuery,
  })

  const deadEndChats = useMemo(
    () =>
      resolveCubeKPIPercentage(
        data,
        ROUTING_MEASURES.DEAD_END_COUNT,
        ROUTING_MEASURES.ROUTING_COUNT
      ),
    [data]
  )

  const totalRecords = useMemo(
    () => resolveTotalRecords(data, ROUTING_MEASURES.DEAD_END_COUNT),
    [data]
  )

  const totalRequests = useMemo(
    () => resolveTotalRecords(data, ROUTING_MEASURES.ROUTING_COUNT),
    [data]
  )

  const score = useMemo(
    () => resolveKPIScore(deadEndChats, KPI_SCORES),
    [deadEndChats]
  )

  const metric = useMemo(() => deadEndChats ?? null, [deadEndChats])

  const hasNoData = useMemo(
    () => !isLoading && isNil(deadEndChats),
    [isLoading, deadEndChats]
  )

  return useMemo(
    () => ({
      score,
      metric,
      isLoading,
      isError,
      hasNoData,
      totalRecords,
      totalRequests,
    }),
    [score, metric, isLoading, isError, hasNoData, totalRecords, totalRequests]
  )
}

export default useDeadEndChats
